import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { combineLatest, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateModule } from '@ngx-translate/core';
import { IAuthenticationService } from '@nimbus-library/features/authentication/services/i-authentication.service';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { ICONS } from '@nimbus-library/features/shared/resources/icons';
import { SharedUIModule } from '@nimbus-library/features/shared/shared.ui.module';
import { FeatureUsageOrigin } from '@nimbus-library/utils/feature-usage-origin';
import { FeatureFlippingService } from '@nimbus-global/features/feature-flipping/feature-flipping.service';
import { Feature } from '@nimbus-global/features/feature-flipping/feature.enum';
import { CallAsPopupComponent } from '@nimbus-global/features/call-as/call-as-popup/call-as-popup.component';
import { CallAsModule } from '@nimbus-global/features/call-as/call-as.module';
import { ICallAsPopupParameters } from '@nimbus-global/features/call-as/domain/i-call-as-popup-parameters';
import { ICallAsPopupTeam } from '@nimbus-global/features/call-as/domain/i-call-as-popup-team';
import { OutboundCallState } from '@nimbus-global/features/shared/models/api/outbound-call-state.model';
import { lastCallOnBehalfTeamIdStorageKey } from '@nimbus-portal/features/user-header/user-header.constants';
import { UserHeaderStateService } from '@nimbus-portal/features/user-header/services/user-header-state.service';
import { OutboundCallStateService } from '@nimbus-portal/common/services/outbound-call-state.service';

@UntilDestroy()
@Component({
  standalone: true,
  selector: 'nimbus-header-bar-quick-actions',
  templateUrl: './header-bar-quick-actions.component.html',
  styleUrl: './header-bar-quick-actions.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, SharedUIModule, CallAsModule, TranslateModule],
})
export class HeaderBarQuickActionsComponent implements OnInit {
  @Input() public origin!: FeatureUsageOrigin;

  public readonly TranslationKeys = TranslationKeys;
  public readonly ICONS = ICONS;

  public teamsOutboundCall$: Observable<ICallAsPopupTeam[]>;
  public isCallAsButtonDisplayed$: Observable<boolean>;
  public isCallAsButtonDisabled$: Observable<boolean>;

  public constructor(
    private readonly userHeaderStateService: UserHeaderStateService,
    private readonly authenticationService: IAuthenticationService,
    private readonly dialogService: MatDialog,
    private readonly outboundCallStateService: OutboundCallStateService,
    private readonly featureFlippingService: FeatureFlippingService,
  ) {}

  public ngOnInit(): void {
    const { tenantId, userId } = this.authenticationService.getUserInfo();
    const isDirectCallHandlingEnabled$ = this.featureFlippingService.isEnabledInFeatureManagementByTenantId$(
      tenantId,
      Feature.DirectCallHandling,
    );
    this.teamsOutboundCall$ = combineLatest([
      this.userHeaderStateService.teams$,
      this.userHeaderStateService.isAvailableForOutboundCall$,
      isDirectCallHandlingEnabled$,
    ]).pipe(
      map(([items, isAvailable, isDirectCallHandlingEnabled]) =>
        items
          // A Direct Call Service proxy team is membership-visible to more than just its owner, so
          // restrict it to the owning agent before any outbound-state handling (mirrors legacy header).
          .filter((x) => !x.teamContactInfo?.isDirectCallService || x.teamContactInfo?.directCallOwningUserO365Id === userId)
          // A user's own Direct Call service is hidden by the backend's default outbound-call license
          // check (it has no service-level license), so it's let through here too when the DCH feature
          // is enabled - it's still a real, callable service for its owning user.
          .filter(
            (x) =>
              x.teamContactInfo?.outboundCallState !== OutboundCallState.Hidden ||
              (isDirectCallHandlingEnabled && x.teamContactInfo?.isDirectCallService),
          )
          .map((x) => ({
            id: x.teamId,
            name: x.teamContactInfo?.serviceName || x.teamContactInfo?.name,
            state: isAvailable ? OutboundCallState.Enabled : OutboundCallState.Disabled,
            isPstnEmpty: !x.teamContactInfo?.pstnNumber,
          })),
      ),
    );
    this.isCallAsButtonDisplayed$ = this.teamsOutboundCall$.pipe(map((items) => !!items.length));
    this.isCallAsButtonDisabled$ = this.outboundCallStateService.isOutboundEnabled$.pipe(map((enabled) => !enabled));
  }

  public openCallAsPopup(): void {
    const { tenantId, userId } = this.authenticationService.getUserInfo();
    this.dialogService
      .open<CallAsPopupComponent, ICallAsPopupParameters>(CallAsPopupComponent, {
        data: {
          tenantId,
          teamId: this.userHeaderStateService.lastCallOnBehalfTeamId$.getValue(),
          currentUserId: userId,
          teams: this.teamsOutboundCall$,
          origin: this.origin,
          closeAfterDial: true,
        },
      })
      .afterClosed()
      .pipe(untilDestroyed(this), filter(Boolean))
      .subscribe((lastCallOnBehalfTeamId: string) => {
        this.userHeaderStateService.lastCallOnBehalfTeamId$.next(lastCallOnBehalfTeamId);
        localStorage.setItem(lastCallOnBehalfTeamIdStorageKey, lastCallOnBehalfTeamId);
      });
  }
}
